import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { SidebarChat, setPendingStreamRun, useSidebarChat } from '@platform/chat';
import { EditableSummary } from '@platform/editor';
import {
  isAwaitingPersistedVersion,
  selectIsSummaryStreaming,
  selectSummaryActiveRun,
  shouldShowStreamingSummary,
} from '@platform/summary-ui';
import type { PageContext, SummaryPageContext } from '@shared/types/pageContext';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiSlice } from '../../store/api/apiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  type SessionArtifactGenerationJobStatus,
  useGenerateSessionArtifactMutation,
  useGetSessionArtifactArcGenerationStatusQuery,
} from '../members/api/sessionApi';
import ArtifactPageWithChatLayout from './components/ArtifactPageWithChatLayout';
import { getArtifactTypeLabel } from './utils/artifactLabels';
import { getArtifactGenerationBlockedMessage } from './utils/generationBlockedMessages';

const GENERATION_STATUS_POLL_INTERVAL_MS = 3000;
const COMPLETED_ARTIFACT_MISSING = 'completed_artifact_missing';

function isActiveGenerationJobStatus(status?: string): boolean {
  return status === 'queued' || status === 'running';
}

function artifactPath(
  artifactId: string,
  conversationId?: string,
  revision?: number | null
): string {
  const params = new URLSearchParams();
  if (typeof revision === 'number' && revision > 0) {
    params.set('revision', String(revision));
  }
  if (conversationId) {
    params.set('conversation_id', conversationId);
  }
  const query = params.toString();
  return `/artifacts/${encodeURIComponent(artifactId)}${query ? `?${query}` : ''}`;
}

function jobErrorMessage(job: SessionArtifactGenerationJobStatus): string {
  if (job.error_message === COMPLETED_ARTIFACT_MISSING) {
    return 'Artifact generation completed without creating an artifact. Start generation again from the member page.';
  }
  return job.error_message || getArtifactGenerationBlockedMessage(job.blocked_reason);
}

function generationRecoveryPath(
  job?: SessionArtifactGenerationJobStatus | null,
  routePersonID?: string
): string {
  const subjectPersonID = job?.subject_person_id?.trim() || routePersonID?.trim();
  if (subjectPersonID) {
    return `/members/${encodeURIComponent(subjectPersonID)}`;
  }
  return '/members';
}

function generationProgressPath(routePersonID: string | undefined, jobID: string): string {
  const query = `job_id=${encodeURIComponent(jobID)}`;
  if (routePersonID?.trim()) {
    return `/members/${encodeURIComponent(routePersonID.trim())}/generating?${query}`;
  }
  return `/artifacts/generating?${query}`;
}

function isDelayedQueuedJob(job?: SessionArtifactGenerationJobStatus | null): boolean {
  return job?.status === 'queued' && Boolean(job.generate_after) && !job.run_id;
}

function sessionInvalidationTags(job: SessionArtifactGenerationJobStatus) {
  const sessionIds = new Set<string>();
  if (job.session_id) {
    sessionIds.add(job.session_id);
  }
  for (const sessionId of job.session_ids ?? []) {
    sessionIds.add(sessionId);
  }
  return [
    { type: 'Sessions' as const, id: 'SESSION_LIST' },
    ...Array.from(sessionIds, (sessionId) => ({ type: 'Sessions' as const, id: sessionId })),
  ];
}

/**
 * Builds typed artifact sources for an arc generation job.
 *
 * `transcript_source_ids` are `session_attachments.id` (see `transcriptSourceIDs` in
 * backend/pkg/sessionartifactgeneration/arc_service.go). They are distinct from IDs
 * in the former `person_transcripts` table. They are also carried in the legacy `transcriptIds` field, which has no type information — so a
 * backend that falls back to that field types them `conversation_transcript` and points a
 * legacy transcript lookup at the wrong table.
 *
 * This is precautionary, not currently active. None of this page's transcript metadata reaches
 * the backend today: it sends a non-empty `transcriptIds` with an empty `transcriptDates`, and
 * `parseTranscriptMetadata` drops the whole block on that length mismatch (pinned by
 * `TestExtractSources_DropsTranscriptMetadataWhenDatesDoNotAlignWithIDs`). Should the dates ever
 * be populated, these typed sources take precedence over the untyped IDs rather than the backend
 * guessing (`extractSources` in backend/pkg/artifact/metadata_extractor.go prefers them).
 */
function sessionArtifactJobSources(
  job: SessionArtifactGenerationJobStatus
): NonNullable<NonNullable<SummaryPageContext['transcriptMetadata']>['sources']> {
  const sources: NonNullable<NonNullable<SummaryPageContext['transcriptMetadata']>['sources']> = [];

  for (const sessionAttachmentID of job.transcript_source_ids ?? []) {
    const value = sessionAttachmentID.trim();
    if (value !== '') {
      sources.push({ type: 'session_transcript', value });
    }
  }
  for (const driveFileID of job.transcript_drive_file_ids ?? []) {
    const value = driveFileID.trim();
    if (value !== '') {
      sources.push({ type: 'google_doc', value });
    }
  }

  return sources;
}

export default function ArtifactGenerationPage(): React.ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { personId: routePersonID } = useParams<{ personId?: string }>();
  const [searchParams] = useSearchParams();
  const jobID = searchParams.get('job_id')?.trim() || '';
  const consumedRunsRef = React.useRef<Set<string>>(new Set());
  const [startGenerationNow, startGenerationNowState] = useGenerateSessionArtifactMutation();
  const [startNowError, setStartNowError] = React.useState<string | null>(null);

  const { data, error, isLoading, isFetching } = useGetSessionArtifactArcGenerationStatusQuery(
    { job_id: jobID },
    {
      pollingInterval: jobID ? GENERATION_STATUS_POLL_INTERVAL_MS : 0,
      skip: !jobID,
    }
  );

  const job = data?.job;
  const profileSlug = job?.profile_slug ?? '';
  const typeLabel = profileSlug ? getArtifactTypeLabel(profileSlug) : 'Artifact';
  const conversationID = job?.conversation_id?.trim() || undefined;
  const runID = job?.run_id?.trim() || undefined;
  const chatContext = React.useMemo<PageContext>(
    () => ({
      pageType: 'summary',
      timestamp: Date.now(),
      transcriptMetadata: job
        ? {
            transcriptIds: job.transcript_source_ids ?? [],
            transcriptDates: [],
            subjectId: job.subject_person_id,
            documentCount: job.transcript_source_ids?.length ?? job.session_ids.length,
            sources: sessionArtifactJobSources(job),
          }
        : undefined,
    }),
    [job]
  );

  const chat = useSidebarChat({
    convId: conversationID,
    profile: profileSlug || 'default',
    autoConnect: Boolean(conversationID),
    displayMode: 'ui',
    context: chatContext,
  });
  const activeRun = useAppSelector(selectSummaryActiveRun(chat.convId));
  const isSummaryStreaming = useAppSelector(selectIsSummaryStreaming(chat.convId));
  const streamingMarkdown = activeRun?.streamingMarkdown ?? '';
  const showStreamingSummary = shouldShowStreamingSummary({
    streamingMarkdown,
    isSummaryStreaming,
    activeRun,
    selection: null,
    artifactDataRevision: null,
  });
  const awaitingPersistedVersion = isAwaitingPersistedVersion(activeRun);

  React.useEffect(() => {
    if (job?.status === 'completed' && job.artifact_id) {
      dispatch(apiSlice.util.invalidateTags(sessionInvalidationTags(job)));
      navigate(artifactPath(job.artifact_id, conversationID), { replace: true });
    }
  }, [conversationID, dispatch, job, navigate]);

  React.useEffect(() => {
    if (!runID || !conversationID || consumedRunsRef.current.has(runID)) {
      return;
    }
    consumedRunsRef.current.add(runID);
    dispatch(
      setPendingStreamRun({
        convId: conversationID,
        runId: runID,
        serverConvId: conversationID,
      })
    );
  }, [conversationID, dispatch, runID]);

  React.useEffect(() => {
    if (!activeRun?.artifactId || !job || !isActiveGenerationJobStatus(job.status)) {
      return;
    }
    dispatch(apiSlice.util.invalidateTags(sessionInvalidationTags(job)));
    navigate(artifactPath(activeRun.artifactId, conversationID, activeRun.revision), {
      replace: true,
    });
  }, [activeRun?.artifactId, activeRun?.revision, conversationID, dispatch, job, navigate]);

  const rightSidebar = conversationID ? (
    <SidebarChat
      storageId={`artifact-generation-${jobID}`}
      entities={chat.entities}
      convId={chat.convId}
      onSendMessage={chat.sendMessage}
      isLoading={chat.isStreaming}
      placeholder="Ask questions or request changes..."
      showHeader={false}
    />
  ) : null;

  const hasRequestError = Boolean(error);
  const failedJob = job && (job.status === 'failed' || job.status === 'blocked') ? job : null;
  const completedWithoutArtifact = job?.status === 'completed' && !job.artifact_id;
  const delayedQueuedJob = isDelayedQueuedJob(job);
  const statusLabel = runID
    ? 'Generating artifact...'
    : delayedQueuedJob
      ? 'Generation scheduled...'
      : 'Starting artifact generation...';
  const recoveryPath = generationRecoveryPath(job, routePersonID);
  const recoveryAction = (
    <Button color="inherit" size="small" onClick={() => navigate(recoveryPath)}>
      Return to sessions
    </Button>
  );
  const handleStartNow = React.useCallback(async () => {
    setStartNowError(null);
    if (!job?.session_id || !job.profile_slug) {
      setStartNowError(
        'Unable to start this scheduled generation because job context is incomplete.'
      );
      return;
    }
    try {
      const generatedArtifact = await startGenerationNow({
        sessionId: job.session_id,
        profile_slug: job.profile_slug,
      }).unwrap();
      if (generatedArtifact.artifact_id) {
        dispatch(apiSlice.util.invalidateTags(sessionInvalidationTags(job)));
        navigate(artifactPath(generatedArtifact.artifact_id, generatedArtifact.conversation_id));
        return;
      }
      if (generatedArtifact.job_id) {
        dispatch(apiSlice.util.invalidateTags(sessionInvalidationTags(job)));
        navigate(generationProgressPath(routePersonID, generatedArtifact.job_id), {
          replace: true,
        });
        return;
      }
      setStartNowError(
        'Generation started, but no job id was returned. Return to sessions and try again.'
      );
    } catch {
      setStartNowError(
        'Unable to start this scheduled generation right now. Return to sessions and try again.'
      );
    }
  }, [dispatch, job, navigate, routePersonID, startGenerationNow]);

  return (
    <ArtifactPageWithChatLayout rightSidebar={rightSidebar}>
      <Box sx={{ bgcolor: 'sunny.100', minHeight: '100vh', p: 3 }}>
        <Paper
          sx={{
            border: '1px solid #D9E3DD',
            borderRadius: '4px',
            boxShadow: '0 18px 40px rgba(24, 44, 32, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 48px)',
            overflow: 'hidden',
            p: 3,
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, mb: 3 }}>
            {!failedJob && !hasRequestError && !completedWithoutArtifact ? (
              <CircularProgress size={18} />
            ) : null}
            <Box>
              <Typography component="h1" sx={{ color: '#2B2E2B', fontSize: 24, fontWeight: 700 }}>
                {typeLabel}
              </Typography>
              <Typography sx={{ color: '#6A746D', fontSize: 14 }}>
                {failedJob || completedWithoutArtifact ? 'Generation stopped.' : statusLabel}
              </Typography>
            </Box>
          </Box>

          {startNowError ? (
            <Alert severity="error" action={recoveryAction} sx={{ mb: 2 }}>
              {startNowError}
            </Alert>
          ) : null}

          {!jobID ? (
            <Alert severity="error" action={recoveryAction}>
              Missing generation job id.
            </Alert>
          ) : hasRequestError ? (
            <Alert severity="error" action={recoveryAction}>
              Unable to load artifact generation status.
            </Alert>
          ) : failedJob ? (
            <Alert severity="error" action={recoveryAction}>
              {jobErrorMessage(failedJob)}
            </Alert>
          ) : completedWithoutArtifact ? (
            <Alert severity="error" action={recoveryAction}>
              Artifact generation completed, but the artifact is not available yet.
            </Alert>
          ) : delayedQueuedJob ? (
            <Box
              sx={{
                alignItems: 'center',
                color: '#6A746D',
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Box>
                <Typography sx={{ color: '#44514A', fontSize: 16, fontWeight: 700 }}>
                  Generation is scheduled to start shortly.
                </Typography>
                <Typography sx={{ color: '#6A746D', fontSize: 14, mt: 0.75 }}>
                  You can start it now instead of waiting.
                </Typography>
                <Button
                  disabled={startGenerationNowState.isLoading}
                  onClick={handleStartNow}
                  sx={{ mt: 2 }}
                  type="button"
                  variant="contained"
                >
                  Start now
                </Button>
              </Box>
            </Box>
          ) : showStreamingSummary ? (
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              <EditableSummary value={streamingMarkdown} readOnly showEditButton={false} />
            </Box>
          ) : (
            <Box
              sx={{
                alignItems: 'center',
                color: '#6A746D',
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Box>
                <Typography sx={{ color: '#44514A', fontSize: 16, fontWeight: 700 }}>
                  {isLoading || isFetching ? 'Checking generation status...' : statusLabel}
                </Typography>
                <Typography sx={{ color: '#6A746D', fontSize: 14, mt: 0.75 }}>
                  {awaitingPersistedVersion
                    ? 'Finalizing the artifact page...'
                    : 'This may take a moment.'}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </ArtifactPageWithChatLayout>
  );
}
